import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Play, Repeat } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Schedules() {
  const { user } = useAuth();
  const isAdmin = ["admin","super_admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ asset_id: "", inspection_type: "routine", interval_days: 30, inspector_id: "", active: true });

  const load = async () => {
    const [s, a, u] = await Promise.all([api.get("/schedules"), api.get("/assets"), api.get("/users")]);
    setItems(s.data); setAssets(a.data); setUsers(u.data);
    if (["admin","super_admin"].includes(user?.role)) { try { const j = await api.get("/admin/jobs"); setJobs(j.data); } catch (e) { console.debug("admin jobs not available", e?.message); } }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.asset_id) return toast.error("Select an asset");
    try { await api.post("/schedules", { ...form, inspector_id: form.inspector_id || null, interval_days: Number(form.interval_days) });
      toast.success("Recurring schedule created"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => { if (!confirm("Delete this schedule?")) return; await api.delete(`/schedules/${id}`); load(); };
  const runNow = async () => { const { data } = await api.post("/admin/jobs/run"); toast.success(`Generated ${data.created} inspection(s)`); load(); };

  return (
    <div className="p-6 md:p-8" data-testid="schedules-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Automation</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Recurring Schedules</h1>
        </div>
        <div className="flex gap-2">
          {isAdmin && <Button data-testid="run-now-btn" onClick={runNow} variant="outline" className="rounded-full"><Play className="w-4 h-4 mr-2" />Run Now</Button>}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-schedule-btn" className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]"><Plus className="w-4 h-4 mr-2" />New Schedule</Button></DialogTrigger>
            <DialogContent className="rounded-md">
              <DialogHeader><DialogTitle className="font-display">Recurring Inspection Schedule</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Asset *</Label>
                  <Select value={form.asset_id} onValueChange={(v) => setForm({ ...form, asset_id: v })}><SelectTrigger className="rounded-md mt-1"><SelectValue placeholder="Select asset" /></SelectTrigger>
                    <SelectContent>{assets.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.asset_tag})</SelectItem>)}</SelectContent></Select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-wider font-semibold">Type</Label>
                    <Select value={form.inspection_type} onValueChange={(v) => setForm({ ...form, inspection_type: v })}><SelectTrigger className="rounded-md mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="routine">Routine</SelectItem><SelectItem value="annual">Annual</SelectItem><SelectItem value="pre_use">Pre-use</SelectItem></SelectContent></Select></div>
                  <div><Label className="text-xs uppercase tracking-wider font-semibold">Every (days) *</Label><Input data-testid="interval-days" type="number" min={1} value={form.interval_days} onChange={(e) => setForm({ ...form, interval_days: e.target.value })} className="rounded-md mt-1" /></div>
                </div>
                <div><Label className="text-xs uppercase tracking-wider font-semibold">Assigned Inspector</Label>
                  <Select value={form.inspector_id} onValueChange={(v) => setForm({ ...form, inspector_id: v })}><SelectTrigger className="rounded-md mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>{users.filter(u => u.role !== 'viewer').map(u => <SelectItem key={u.id} value={u.id}>{u.name} — {u.role}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <DialogFooter><Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-schedule-btn" onClick={save} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {jobs && (
        <div className="bg-white border border-gray-200 p-4 mb-4 text-sm flex flex-wrap items-center gap-4" data-testid="jobs-status">
          <span className="inline-flex items-center gap-2"><Repeat className="w-4 h-4 text-[#01ADEF]" /><b>Scheduler:</b> {jobs.scheduler_running ? "Running" : "Stopped"}</span>
          <span><b>Last run:</b> {jobs.stats?.last_run || "—"}</span>
          <span><b>Total runs:</b> {jobs.stats?.runs}</span>
          <span><b>Inspections created:</b> {jobs.stats?.created}</span>
        </div>
      )}

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Asset</Th><Th>Type</Th><Th>Every</Th><Th>Next Run</Th><Th>Last Run</Th><Th></Th></tr></thead>
          <tbody data-testid="schedules-table">
            {items.length === 0 ? <tr><td colSpan={6} className="text-center py-10 text-gray-500">No recurring schedules yet.</td></tr>
              : items.map(s => (
                <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`schedule-row-${s.id}`}>
                  <Td><div className="font-semibold">{s.asset_name}</div><div className="text-xs text-gray-500 font-mono">{s.asset_tag}</div></Td>
                  <Td className="capitalize">{s.inspection_type?.replace('_',' ')}</Td>
                  <Td className="font-mono">{s.interval_days}d</Td>
                  <Td className="font-mono text-xs">{s.next_run?.slice(0,16).replace('T',' ')}</Td>
                  <Td className="font-mono text-xs">{s.last_run ? s.last_run.slice(0,16).replace('T',' ') : '—'}</Td>
                  <Td>{isAdmin && <button onClick={() => remove(s.id)} className="text-gray-400 hover:text-[#DC2626]"><Trash2 className="w-4 h-4" /></button>}</Td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
